import { MainContainer } from "../../components/layout";
import {
  Reducer,
  useReducer,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import AppContext from "../../state/AppContext";
import { useTranslation } from "react-i18next";
import SmallLoader from "../../components/loaders/SmallLoader";
import { useAuth } from "react-oidc-context";
import managerDashboardApi from "../../api/dashboard/managerDashboardApi";
import { FormListingType } from "../../types/forms/FormListingType";
import JourneyListingUserDetailDto from "../../types/dtos/dashboards/manager/listings/journey/JourneyListingUserDetailDto";
import JourneyListingDto from "../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import JourneyStatisticsBar from "../../components/manager-dashboard/journey-listing/JourneyStatisticsBar";
import JourneyListingSearchAndFilter from "../../components/manager-dashboard/journey-listing/JourneyListingSearchAndFilter";
import JourneyUserView from "../../components/manager-dashboard/journey-listing/user-view/JourneyUserView";
import JourneyListTableView from "../../components/manager-dashboard/journey-listing/table-view/JourneyListTableView";
import { useNavigate, useParams } from "react-router-dom";
import myDashboardApi from "../../api/dashboard/myDashboardApi";
import dualPrepJourneyHelper from "../../helpers/dualPrepJourneyHelper";
import UserContext from "../../state/UserContext";
import { JourneyFormButtonArg } from "../../types/dashboard/JourneyFormDropDownItems";
import AppRoutes from "../AppRoutes";
import {
  faMessagesQuestion,
  faSquarePollHorizontal,
  faExpandAlt,
  faCompressAlt,
} from "@fortawesome/pro-light-svg-icons";
import ListViewToggle from "../../components/manager-dashboard/shared/ListViewToggle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface ManagerJourneyListingPageState {
  isLoading: boolean;
  users: JourneyListingUserDetailDto[] | null;
  journeys: JourneyListingDto[] | null;
  hasUpcomingJourneys: boolean | null;
  originalUsers: JourneyListingUserDetailDto[] | null;
  originalJourneys: JourneyListingDto[] | null;
}

interface ManagerJourneyListingPageProps {
  formType: FormListingType;
}

const ManagerJourneyListingPage = ({
  formType,
}: ManagerJourneyListingPageProps) => {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const auth = useAuth();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const dashboardApi = new myDashboardApi(auth.user?.access_token);
  const managerApi = new managerDashboardApi(auth.user?.access_token);

  const { preselectedEmployeeId } = useParams();

  const [state, setState] = useReducer<
    Reducer<
      ManagerJourneyListingPageState,
      Partial<ManagerJourneyListingPageState>
    >
  >((state, newState) => ({ ...state, ...newState }), {
    isLoading: false,
    users: null,
    journeys: null,
    hasUpcomingJourneys: null,
    originalUsers: null,
    originalJourneys: null,
  });

  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("All Statuses");
  const [selectedDateRange, setSelectedDateRange] =
    useState<string>("In the last year");
  const [view, setView] = useState<string>("Table View");
  const [pageNumber, setPageNumber] = useState(1);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [expandAllUsers, setExpandAllUsers] = useState(false);
  const [previousView, setPreviousView] = useState<string | null>(null);

  const populateSearchBarWithPreselectedEmployee = (
    users: JourneyListingUserDetailDto[]
  ) => {
    const preselectedEmployeeName = users.find(
      (x) => x.employeeId === parseInt(preselectedEmployeeId!)
    )?.employeeFullName;
    if (preselectedEmployeeName != null) {
      setSearchTerm(preselectedEmployeeName);
    }
  };

  useEffect(() => {
    setState({ isLoading: true });

    const translationKey =
      formType === "CONVERSATIONS"
        ? "Nav.LargeScreen.ManagerDashboard.Conversations"
        : "Nav.LargeScreen.ManagerDashboard.PulseChecks";

    appContext.setPageIcon(
      formType === "CONVERSATIONS" ? faMessagesQuestion : faSquarePollHorizontal
    );

    appContext.setPageTitle(
      t("Nav.LargeScreen.ManagerDashboard.Root") + ": " + t(translationKey)
    );

    managerApi.getJourneyFormDataForPeopleManagedByLoggedInUser(
      formType,
      (data) => {
        // Sort the journeys to place upcoming journeys at the top if present
        let sortedJourneys = data.journeys;
        if (data.hasUpcomingJourneys) {
          // Split journeys into upcoming and non-upcoming
          const upcomingJourneys = data.journeys.filter(
            (journey) => journey.overallStatus === "UPCOMING"
          );
          const otherJourneys = data.journeys.filter(
            (journey) => journey.overallStatus !== "UPCOMING"
          );

          // Combine with upcoming journeys first, preserving the original order within each group
          sortedJourneys = [...upcomingJourneys, ...otherJourneys];
        }

        setState({
          isLoading: false,
          users: data.users,
          journeys: sortedJourneys,
          hasUpcomingJourneys: data.hasUpcomingJourneys,
          originalUsers: data.users,
          originalJourneys: sortedJourneys, // Also update originalJourneys to maintain the order
        });

        if (preselectedEmployeeId && data.users.length > 0) {
          populateSearchBarWithPreselectedEmployee(data.users);
        }
      },
      (error) => {
        console.error("Error loading manager data", error);
      }
    );
  }, []);

  useEffect(() => {
    if (state.originalJourneys) {
      applyFilters(searchTerm, selectedStatus, selectedDateRange);
    }
  }, [selectedStatus, searchTerm, selectedDateRange, state.originalJourneys]);

  const hasPreventJourneySwitchConfigEnabled =
    userContext.user.client.moduleConfigs.findIndex(
      (cfg) =>
        cfg.key === "PreventManualJourneySwitching" &&
        cfg.value.trim().toLowerCase() === "true"
    ) >= 0;

  // Helper function to get the relevant date for filtering
  const getJourneyFilterDate = (journey: JourneyListingDto): Date | null => {
    // Use last modified date if it exists, otherwise use send date
    if (journey.dateLastUpdated) {
      return new Date(journey.dateLastUpdated);
    }
    if (journey.dateSent) {
      return new Date(journey.dateSent);
    }
    return null;
  };

  // Helper function to check if a journey falls within the selected date range
  const isWithinDateRange = (
    journey: JourneyListingDto,
    dateRange: string
  ): boolean => {
    // Always show "Upcoming" journeys regardless of date filter
    if (journey.overallStatus === "UPCOMING") {
      return true;
    }

    const journeyDate = getJourneyFilterDate(journey);
    if (!journeyDate) return false;

    const now = new Date();
    let cutoffDate: Date;

    switch (dateRange) {
      case "Last six months":
        cutoffDate = new Date();
        cutoffDate.setMonth(now.getMonth() - 6);
        break;
      case "In the last year":
        cutoffDate = new Date();
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      case "All time":
        return true; // No date filtering for "All time"
      default:
        return true;
    }

    return journeyDate >= cutoffDate;
  };

  // Extract common filtering logic
  const applyFilters = (
    searchText: string | null,
    statusFilter: string,
    dateRangeFilter: string
  ) => {
    // Reset pagination to first page whenever filters change
    setPageNumber(1);

    if (!state.originalJourneys || !state.originalUsers) return;

    // Convert display status to the internal status code
    const getStatusCode = (displayStatus: string): string => {
      switch (displayStatus) {
        case "Not Started":
          return "NOT-STARTED";
        case "In Progress":
          return "IN-PROGRESS";
        case "Completed":
          return "COMPLETED";
        case "Upcoming":
          return "UPCOMING";
        default:
          return displayStatus;
      }
    };

    const statusCode = getStatusCode(statusFilter);

    // Filter journeys by status and date range
    const filteredJourneys = state.originalJourneys.filter(
      (journey) =>
        journey.overallStatus !== "UNKNOWN" &&
        (statusFilter === "All Statuses" ||
          journey.overallStatus === statusCode) &&
        isWithinDateRange(journey, dateRangeFilter)
    );

    // Get all user IDs that have matching journeys
    const userIdsWithMatchingJourneys = new Set(
      filteredJourneys.map((journey) => journey.subjectEmployeeId)
    );

    // If no search term, filter users based on whether they have matching journeys
    if (!searchText || searchText.trim() === "") {
      const usersWithMatchingJourneys = state.originalUsers.filter((user) =>
        userIdsWithMatchingJourneys.has(user.employeeId)
      );

      setState({
        users: usersWithMatchingJourneys,
        journeys: filteredJourneys,
      });
      return;
    }

    // Find users that match the search term
    const usersMatchingSearch = state.originalUsers.filter((user) =>
      user.employeeFullName.toLowerCase().includes(searchText.toLowerCase())
    );

    // Get IDs of users matching the search
    const userIdsMatchingSearch = new Set(
      usersMatchingSearch.map((user) => user.employeeId)
    );

    // Filter journeys by title (in addition to status)
    const journeysMatchingSearch = filteredJourneys.filter((journey) =>
      journey.title.toLowerCase().includes(searchText.toLowerCase())
    );

    // Get a list of user IDs who have journeys matching the search
    const userIdsWithMatchingSearchJourneys = new Set(
      journeysMatchingSearch.map((journey) => journey.subjectEmployeeId)
    );

    // Combine both sets to get all users we need to display
    const allMatchingUserIds = new Set([
      ...Array.from(userIdsMatchingSearch),
      ...Array.from(userIdsWithMatchingSearchJourneys),
    ]);

    // Filter users to include only those matching search or having matching journeys
    const filteredUsers = state.originalUsers.filter((user) =>
      allMatchingUserIds.has(user.employeeId)
    );

    // For the journeys, we need to include:
    // 1. ALL journeys for users whose names match the search
    // 2. Only matching journeys for users whose names don't match the search
    const relevantJourneys = filteredJourneys.filter((journey) => {
      // If this journey belongs to a user whose name matches the search, include it
      if (userIdsMatchingSearch.has(journey.subjectEmployeeId)) {
        return true;
      }

      // If this journey's title matches the search, include it
      if (journey.title.toLowerCase().includes(searchText.toLowerCase())) {
        return true;
      }

      // Otherwise, exclude it
      return false;
    });

    setState({
      users: filteredUsers,
      journeys: relevantJourneys,
    });
  };

  const handleSearchTermChange = (newSearchTerm: string | null) => {
    // Reset pagination to first page whenever search terms change
    setPageNumber(1);

    setSearchTerm(newSearchTerm);
    applyFilters(newSearchTerm, selectedStatus, selectedDateRange);
  };

  // Handle View/Update button clicks
  const handleUpdateClick = (journey: JourneyListingDto) => {
    const goToDashboard =
      journey.latestAnswerSet?.guidId == null ||
      journey.latestAnswerSet?.statusEnum === "PARTIALLY-SAVED-JOURNEY";

    if (goToDashboard) {
      setCurrentJourneyAndRedirectToDashboard(journey.journeyReference);
    } else {
      navigateToCollabDoc(journey.latestAnswerSet.guidId);
    }
  };

  const handleViewClick = (journey: JourneyListingDto) => {
    navigateToCollabDoc(journey.latestAnswerSet.guidId);
  };

  const handleDualPrepItemClick = (
    user: JourneyListingUserDetailDto,
    journey: JourneyListingDto,
    arg: JourneyFormButtonArg
  ) => {
    switch (arg) {
      case "COMPLETE-MAN-PLANNING":
        // Check whether the manager planning exists
        const doesManPlanningExist = journey.answerSets.some(
          (x) => x.isDualPrepOtherAnswerSet
        );
        if (!doesManPlanningExist) {
          // If it doesn't exist then we need to create it and then navigate to it
          const dualPrepHelper = new dualPrepJourneyHelper(
            managerApi,
            userContext
          );
          dualPrepHelper.openNewCollabDocByJourneyRef(
            user.employeeId,
            journey.journeyReference,
            (destinationUrl: string) => {
              navigate(destinationUrl);
            }
          );
        } else {
          // Navigate to the manager planning document
          const manPlanning = journey.answerSets.find(
            (x) => x.isDualPrepOtherAnswerSet
          );
          navigateToCollabDoc(manPlanning?.guidId);
        }
        break;
      case "VIEW-MAN-PLANNING":
        const manPlanning = journey.answerSets.find(
          (x) => x.isDualPrepOtherAnswerSet
        );
        navigateToCollabDoc(manPlanning?.guidId);
        break;
      case "UPDATE-MEETING":
      case "VIEW-MEETING":
        const meetingDoc = journey.answerSets.find(
          (x) => x.isDualPrepMeetingDocAnswerSet
        );
        navigateToCollabDoc(meetingDoc?.guidId);
        break;
    }
  };

  const navigateToCollabDoc = (guidId?: string) => {
    if (!guidId) return null;

    const redirectUrl = AppRoutes.collaborativeDocument.generateRoute(
      guidId,
      false,
      null
    );
    navigate(redirectUrl);
  };

  const setCurrentJourneyAndRedirectToDashboard = (journeyRef: string) => {
    dashboardApi.assignDifferentJourneyToUser(
      null,
      journeyRef,
      () => {
        navigate(AppRoutes.yourJourney.root);
      },
      (error) => {
        console.error("Error setting current journey", error);
      }
    );
  };

  // Handler for expanded sections change from JourneyUserView
  const handleExpandedSectionsChange = (sections: string[]) => {
    setExpandedSections(sections);
    // If sections are manually changed, reset expandAllUsers to prevent conflicts
    if (sections.length > 0 && sections.length < (state.users?.length || 0)) {
      setExpandAllUsers(false);
    }
  };

  // Toggle expand all users
  const handleToggleExpandAllUsers = useCallback(() => {
    if (expandAllUsers) {
      // If expand all is currently active, collapse all
      setExpandAllUsers(false);
      setExpandedSections([]);
    } else {
      // If not all expanded, expand all
      setExpandedSections([]);
      setExpandAllUsers(true);
    }
  }, [expandAllUsers]);

  // Check if all users are expanded to determine if we should show "Collapse All"
  const shouldShowCollapseAll = expandAllUsers || expandedSections.length > 0;

  // Handle view toggle with reset of expansion states
  const handleViewToggle = useCallback(
    (newView: string) => {
      if (newView === view) return;

      setPreviousView(view);
      setView(newView);

      // Reset expand states when switching views
      setExpandAllUsers(false);
      setExpandedSections([]);
    },
    [view]
  );

  return (
    <>
      {state.isLoading && (
        <div className="pt-5">
          <SmallLoader />
          <p className="text-center pt-1 text-[#959595] text-sm">
            {t("Common.Loading")}...
          </p>
        </div>
      )}
      {!state.isLoading && (
        <div className="px-2 sm:px-4 md:px-6">
          <JourneyStatisticsBar
            journeys={state.journeys}
            originalJourneys={state.originalJourneys}
            hasUpcomingJourneys={state.hasUpcomingJourneys}
            formType={formType}
            selectedStatus={selectedStatus}
            searchTerm={searchTerm}
            selectedDateRange={selectedDateRange}
            onStatusChange={setSelectedStatus}
          />
          <JourneyListingSearchAndFilter
            onSearch={handleSearchTermChange}
            isLoading={state.isLoading}
            loaderColor="#000"
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            hasUpcomingJourneys={state.hasUpcomingJourneys}
            searchTerm={searchTerm}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
          />
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3 sm:gap-0">
            <ListViewToggle onToggle={handleViewToggle} />

            {view === "User View" && state.users && state.users.length > 0 && (
              <button
                className="btn-secondary flex items-center text-sm justify-center sm:justify-start"
                onClick={handleToggleExpandAllUsers}
                title={shouldShowCollapseAll ? "Collapse All" : "Expand All"}
              >
                <FontAwesomeIcon
                  icon={shouldShowCollapseAll ? faCompressAlt : faExpandAlt}
                  className="mr-2"
                />
                {shouldShowCollapseAll ? "Collapse All" : "Expand All"}
              </button>
            )}
          </div>

          {view === "User View" ? (
            <JourneyUserView
              users={state.users}
              journeys={state.journeys}
              hasPreventJourneySwitchConfigEnabled={
                hasPreventJourneySwitchConfigEnabled
              }
              onUpdateClick={handleUpdateClick}
              onViewClick={handleViewClick}
              onDualPrepItemClick={handleDualPrepItemClick}
              expandAll={expandAllUsers}
              onExpandedSectionsChange={handleExpandedSectionsChange}
              expandedSections={expandedSections}
            />
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <JourneyListTableView
                users={state.users}
                journeys={state.journeys}
                hasPreventJourneySwitchConfigEnabled={
                  hasPreventJourneySwitchConfigEnabled
                }
                onUpdateClick={handleUpdateClick}
                onViewClick={handleViewClick}
                onDualPrepItemClick={handleDualPrepItemClick}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ManagerJourneyListingPage;
